import React, { createContext, useContext, useEffect, useState } from "react";
import { apiUrl } from "@/lib/apiConfig";
import crimsonTheme from "../crimson/theme.config";

const ThemeContext = createContext();
const themes = { crimson: crimsonTheme };

const applyFavicon = (url) => {
  if (!url) return;
  const rels = ["icon", "shortcut icon", "apple-touch-icon"];
  rels.forEach((rel) => {
    let link = document.querySelector(`link[rel='${rel}']`);
    if (!link) {
      link = document.createElement("link");
      link.rel = rel;
      document.head.appendChild(link);
    }
    link.type = "image/png";
    link.href = url;
  });
};

export const ThemeProvider = ({ children }) => {
  const [siteAssets, setSiteAssets] = useState({});

  useEffect(() => {
    const theme = crimsonTheme;
    const root = document.documentElement;

    Object.entries(theme.colors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key}`, value);
    });
    root.style.setProperty("--font-display", theme.typography.fontDisplay);
    root.style.setProperty("--font-body", theme.typography.fontBody);
    root.style.setProperty("--font-accent", theme.typography.fontAccent);
    root.style.setProperty("--font-code", theme.typography.fontCode);
    Object.entries(theme.borderRadius).forEach(([key, value]) => {
      root.style.setProperty(`--radius-${key}`, value);
    });
    Object.entries(theme.shadows).forEach(([key, value]) => {
      root.style.setProperty(`--shadow-${key}`, value);
    });
    root.setAttribute("data-theme", "crimson");
    document.body.style.backgroundColor = theme.colors.background;
    document.body.style.color = theme.colors.textPrimary;
  }, []);

  useEffect(() => {
    const fetchAssets = async () => {
      try {
        const response = await fetch(apiUrl("/public/settings/site_assets"));
        const data = await response.json();
        const assets = data.value || {};
        setSiteAssets(assets);
        const faviconUrl = assets.favicon_url && assets.favicon_updated_at
          ? `${assets.favicon_url}${assets.favicon_url.includes("?") ? "&" : "?"}v=${encodeURIComponent(assets.favicon_updated_at)}`
          : assets.favicon_url;
        applyFavicon(faviconUrl);
      } catch {
        setSiteAssets({});
      }
    };
    fetchAssets();
  }, []);

  const value = {
    activeTheme: "crimson",
    theme: crimsonTheme,
    themes,
    siteAssets,
    setActiveTheme: () => {},
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
};

export default ThemeContext;
